import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { emptyFor } from './scope-notice';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { useCreateIntent } from '../app/create-intent';
import { formatDate, formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, Panel, type BadgeTone } from '../ui/Page';
import {
  ArrowGlyph,
  CalendarGlyph,
  ClockGlyph,
  ConversionGlyph,
  FunnelGlyph,
  GlobeGlyph,
  ExportGlyph,
  ImportGlyph,
  PeopleGlyph,
  ResponseGlyph,
  SearchGlyph,
  TargetGlyph,
  UnknownGlyph,
  WaitingGlyph,
} from './BoardIcons';
import { DataTable, type Column } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { exportEntity, searchEntity, type FilterGroup } from './api';
import {
  formatMinutes,
  formatRate,
  leadActivity,
  leadSummary,
  type LeadSummary,
} from './leads-api';
import type { LeadRow } from './EntityListPage';
import { NewLeadDialog } from './NewLeadDialog';

/**
 * Leads.
 *
 * The screen a salesperson opens first in the morning, so it answers that morning's questions before
 * it shows a table: is anybody waiting on us, is what arrives worth working, are we closing it, and
 * which channel the closed ones came from. A count of leads answers none of those, which is why it
 * is the smallest figure here.
 *
 * The status is a bar rather than a badge. A lead's status is a position in a funnel, and a colour
 * says "which one" where a bar says "how far" — which is the question somebody scanning fifty rows
 * is actually asking. A disqualified lead gets no bar at all: drawing it halfway would say it got
 * halfway.
 *
 * Every figure is scoped exactly as the list is, on the server. A summary counting the whole tenant
 * above a list showing one rep's own leads would be two screens disagreeing.
 */

const LEAD_TONE: Record<string, BadgeTone> = {
  NEW: 'info',
  CONTACTED: 'warning',
  QUALIFIED: 'positive',
  UNQUALIFIED: 'neutral',
  CONVERTED: 'positive',
};

const PERIODS = [7, 30, 90];

/** Mirrors the server's own cap, so the message can name the number the file actually stopped at. */
const EXPORT_LIMIT = 5000;

/** What the quick filters mean, as filters the server already understands. */
type Quick = 'all' | 'mine' | 'new' | 'qualified' | 'converted';

export function LeadsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [days, setDays] = useState(30);
  const [quick, setQuick] = useState<Quick>('all');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [creating, setCreating] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Arriving from the home screen's Criar menu opens the same dialog the button here opens.
  useCreateIntent(() => setCreating(true));

  const summary = useQuery({
    queryKey: ['lead-summary', days],
    queryFn: () => leadSummary(days),
  });

  const filter = useMemo(
    () => buildFilter({ quick, term: applied, ownerId: user?.id ?? null }),
    [quick, applied, user?.id],
  );

  const leads = useQuery({
    queryKey: ['leads', quick, applied],
    queryFn: () =>
      searchEntity<LeadRow>('leads', {
        ...(filter ? { filter } : {}),
        sort: { field: 'created_at', direction: 'desc' },
        limit: 50,
        includeCount: true,
      }),
  });

  const rows = leads.data?.results ?? [];

  // One request for the whole page rather than one per row: fifty round trips to fill a column is
  // the N+1 the spec names outright.
  const activity = useQuery({
    queryKey: ['lead-activity', rows.map((lead) => lead.id).join(',')],
    queryFn: () => leadActivity(rows.map((lead) => lead.id)),
    enabled: rows.length > 0,
  });

  const { options: people } = useMembers();

  const exporting = useMutation({
    mutationFn: () =>
      exportEntity('leads', {
        ...(filter ? { filter } : {}),
        sort: { field: 'created_at', direction: 'desc' },
      }),
    onSuccess: (outcome) => {
      // A file cut short is said out loud rather than presented as the whole list.
      setExportError(
        outcome.truncated ? t('crm.exportTruncated').replace('{n}', String(EXPORT_LIMIT)) : null,
      );
    },
    onError: (caught: unknown) => setExportError(messageFor(caught)),
  });

  const lastByLead = useMemo(
    () => new Map((activity.data?.activity ?? []).map((entry) => [entry.leadId, entry])),
    [activity.data],
  );

  const owner = (id: string | null): { name: string; url?: string | null; muted: boolean } => {
    const found = id === null ? undefined : people.find((person) => person.id === id);

    return found
      ? { name: found.name, url: found.avatarUrl, muted: false }
      : { name: t('crm.unassigned'), muted: true };
  };

  /*
   * Six columns by default and four more a click away.
   *
   * The default set is what somebody working the list in the morning needs: who it is, how far it
   * got, whether anybody answered, whose it is, what it scored and when it arrived. E-mail, phone,
   * the company and the source are real fields people do sort and scan by, but showing ten columns
   * to everybody makes the six that matter unreadable — so they are declared `hidden` and the
   * chooser offers them, remembered on the account.
   */
  const columns: Column<LeadRow>[] = [
    {
      key: 'name',
      header: t('crm.field.name'),
      text: (lead) => `${lead.name} ${lead.company_name ?? ''} ${lead.email ?? ''}`,
      render: (lead) => (
        <span className="cell-stack">
          <span className="strong">{lead.name}</span>
          <span className="muted small">
            {lead.company_name ?? lead.email ?? lead.phone ?? '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (lead) => t(`crm.lead.${lead.status}`),
      /*
       * A pill with a dot in the status's own colour.
       *
       * It replaced a progress bar that drew a lead's position in the funnel. The bar was honest
       * and answered a question nobody was asking: scanning fifty rows, somebody wants to know
       * *which* status, not how far along it is — and a bar takes a column's worth of width to say
       * less than a word does.
       */
      render: (lead) => (
        <span className={`pill pill--${lead.status.toLowerCase()}`}>
          <span className="pill__dot" aria-hidden="true" />
          {t(`crm.lead.${lead.status}`)}
        </span>
      ),
    },
    {
      key: 'activity',
      header: t('crm.lastActivity'),
      text: (lead) => (lastByLead.get(lead.id) ? '' : t('crm.noActivity')),
      render: (lead) => {
        const last = lastByLead.get(lead.id);

        if (!last) {
          // The actionable cell on this screen: a lead nobody has answered.
          return (
            <span className="cell-pending">
              <ClockGlyph />
              {t('crm.noActivity')}
            </span>
          );
        }

        return (
          <span className="cell-stack">
            <span className="small cell-inline">
              <CalendarGlyph />
              {t(`activity.${last.type}`)}
              {last.subject ? ` · ${last.subject}` : ''}
            </span>
            <span className="muted small">{formatRelative(last.at)}</span>
          </span>
        );
      },
    },
    {
      key: 'owner',
      header: t('crm.owner'),
      text: (lead) => owner(lead.owner_id ?? null).name,
      render: (lead) => <Person size="xs" {...owner(lead.owner_id ?? null)} />,
    },
    {
      key: 'score',
      header: t('crm.field.score'),
      align: 'end',
      text: (lead) => String(lead.score ?? 0),
      render: (lead) => formatNumber(lead.score ?? 0),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      /* Date over time: the day is what somebody scans by, the hour is the detail under it. */
      render: (lead) => (
        <span className="cell-stack">
          <span className="small">{formatDate(lead.created_at)}</span>
          <span className="muted small">{timeOf(lead.created_at)}</span>
        </span>
      ),
    },
    {
      key: 'email',
      header: t('crm.field.email'),
      hidden: true,
      render: (lead) => lead.email ?? '—',
    },
    {
      key: 'phone',
      header: t('crm.field.phone'),
      hidden: true,
      render: (lead) => lead.phone ?? '—',
    },
    {
      key: 'company_name',
      header: t('crm.field.company'),
      hidden: true,
      render: (lead) => lead.company_name ?? '—',
    },
    {
      key: 'source',
      header: t('record.field.source'),
      hidden: true,
      text: (lead) => lead.source ?? '',
      render: (lead) =>
        lead.source ? <Badge tone={LEAD_TONE[lead.status] ?? 'neutral'}>{lead.source}</Badge> : '—',
    },
  ];

  const total = leads.data?.total ?? null;

  return (
    <div className="view view--wide leads-page">
      <header className="board-head">
        <div>
          <h1>{t('nav.leads')}</h1>
          <p className="muted">{t('crm.leadsDescription')}</p>
        </div>

        <div className="cell-inline">
          <label className="board-search board-search--select">
            <CalendarGlyph />
            <select
              className="bare-select"
              aria-label={t('crm.period')}
              value={days}
              onChange={(changeEvent) => setDays(Number(changeEvent.target.value))}
            >
              {PERIODS.map((period) => (
                <option key={period} value={period}>
                  {t('crm.lastDays').replace('{n}', String(period))}
                </option>
              ))}
            </select>
          </label>

          <Link to="/crm/importar" className="button button--quiet">
            <ImportGlyph />
            {t('crm.import')}
          </Link>

          <button
            type="button"
            className="button button--primary"
            onClick={() => setCreating(true)}
          >
            + {t('crm.newLead')}
          </button>
        </div>
      </header>

      <NewLeadDialog open={creating} onClose={() => setCreating(false)} />

      {summary.isLoading ? (
        <Loading />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : summary.data ? (
        <Figures summary={summary.data} onShowUntouched={() => setQuick('new')} />
      ) : null}

      <Panel>
        <div className="panel-toolbar">
          <span className="panel-toolbar__title">
            <h2>{t('crm.leadList')}</h2>
            {total !== null && <span className="board__count">{formatNumber(total)}</span>}
          </span>

          <form
            className="panel-toolbar__actions"
            onSubmit={(formEvent) => {
              formEvent.preventDefault();
              setApplied(search.trim());
            }}
          >
            <label className="board-search">
              <SearchGlyph />
              <input
                type="search"
                value={search}
                placeholder={t('crm.searchPlaceholder')}
                aria-label={t('crm.searchPlaceholder')}
                onChange={(changeEvent) => setSearch(changeEvent.target.value)}
              />
            </label>

            <button type="submit" className="button button--quiet">
              {t('crm.search')}
            </button>

            {/*
              The file carries the list as it is filtered, not the fifty rows the browser happens
              to hold. The server applies the same scope and audits it.
            */}
            <button
              type="button"
              className="button button--quiet"
              disabled={exporting.isPending}
              onClick={() => exporting.mutate()}
            >
              <ExportGlyph />
              {exporting.isPending ? t('crm.exporting') : t('crm.export')}
            </button>
          </form>
        </div>

        {exportError && (
          <p className="alert" role="alert">
            {exportError}
          </p>
        )}

        {/*
          The quick filters as tabs rather than chips.
          They are five views of one list, which is what a tab strip means; chips read as things
          that combine, and these do not.
        */}
        <nav className="tabs" aria-label={t('crm.leadList')}>
          {(['all', 'mine', 'new', 'qualified', 'converted'] as Quick[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`tab${quick === key ? ' is-active' : ''}`}
              onClick={() => setQuick(key)}
            >
              {t(`crm.quick.${key}`)}
            </button>
          ))}
        </nav>

        <DataTable
          tableKey="leads"
          rows={leads.data?.results}
          columns={columns}
          rowKey={(lead) => lead.id}
          isLoading={leads.isLoading}
          error={leads.error}
          onRetry={() => void leads.refetch()}
          onRowClick={(lead) => navigate(`/crm/registro/leads/${lead.id}`)}
          empty={emptyFor(leads.data?.visibility, {
            title: t('crm.noLeads'),
            description: t('crm.emptyBody'),
          })}
        />

        {total !== null && rows.length > 0 && (
          <p className="muted small">
            {t('crm.showing').replace('{n}', String(rows.length)).replace('{total}', String(total))}
          </p>
        )}
      </Panel>
    </div>
  );
}

/** The hour under the date, in the reader's own zone. */
function timeOf(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * The figures, in the order the questions come.
 *
 * "Aguardando resposta" is first, carries an accent down its edge when anything has gone stale, and
 * is a button — because it is the only one here that is a to-do list rather than a report, and a
 * number somebody cannot act on from where they are reading it is a number they stop reading.
 *
 * Underneath, the two questions a morning actually turns into work: where everything is sitting,
 * and which channel is worth the effort.
 */
function Figures({
  summary,
  onShowUntouched,
}: {
  summary: LeadSummary;
  onShowUntouched: () => void;
}): React.JSX.Element {
  const waiting = summary.untouched;

  return (
    <>
      <div className="metrics metrics--icons">
        <button
          type="button"
          className={`metric metric--icon metric--action${
            waiting.stale > 0 ? ' metric--waiting' : ''
          }`}
          onClick={onShowUntouched}
        >
          <span
            className={`metric__icon metric__icon--${waiting.stale > 0 ? 'warning' : 'neutral'}`}
            aria-hidden="true"
          >
            <WaitingGlyph />
          </span>

          <span className="cell-stack">
            <span className="metric__label">{t('crm.kpi.untouched')}</span>
            <span className="metric__value">{formatNumber(waiting.total)}</span>
            <span className="muted small">
              {waiting.stale > 0
                ? t('crm.kpi.untouchedStale').replace('{n}', String(waiting.stale))
                : t('crm.kpi.untouchedClear')}
            </span>
          </span>
        </button>

        <Figure
          icon={<ResponseGlyph />}
          tone="neutral"
          label={t('crm.kpi.responseTime')}
          value={formatMinutes(summary.response.median)}
          hint={
            summary.response.measured === 0
              ? t('crm.kpi.responseNone')
              : t('crm.kpi.responseAverage').replace(
                  '{value}',
                  formatMinutes(summary.response.average),
                )
          }
        />

        <Figure
          icon={<PeopleGlyph />}
          tone="neutral"
          label={t('crm.kpi.arrived')}
          value={formatNumber(summary.total)}
          hint={t('crm.kpi.inPeriod').replace('{n}', String(summary.period.days))}
        />

        <Figure
          icon={<FunnelGlyph />}
          tone="info"
          label={t('crm.kpi.qualification')}
          value={formatRate(summary.rates.qualification)}
          hint={t('crm.kpi.qualificationHint')}
        />

        <Figure
          icon={<ConversionGlyph />}
          tone="positive"
          label={t('crm.kpi.conversion')}
          value={formatRate(summary.rates.conversion)}
          hint={t('crm.kpi.conversionHint')}
        />

        <Figure
          icon={<TargetGlyph />}
          tone="neutral"
          label={t('crm.kpi.close')}
          value={formatRate(summary.rates.close)}
          hint={t('crm.kpi.closeHint')}
        />
      </div>

      {/*
        Said once, in a line, only when it is true.
        A banner that is always there is a banner nobody reads, so this appears only when somebody
        has actually been left waiting — and it takes you straight to them.
      */}
      {waiting.stale > 0 && (
        <div className="notice notice--warning" role="status">
          <span className="cell-inline">
            <WaitingGlyph />
            {t('crm.kpi.staleBanner').replace('{n}', String(waiting.stale))}
          </span>

          <button type="button" className="notice__action" onClick={onShowUntouched}>
            {t('crm.kpi.seePending')}
            <ArrowGlyph />
          </button>
        </div>
      )}

      <div className="home-grid">
        <Panel>
          <div className="panel-toolbar">
            <span className="panel-toolbar__title">
              <h2>{t('crm.kpi.funnel')}</h2>
            </span>
            <span className="muted small">
              {t('crm.kpi.inPeriodCount').replace('{n}', String(summary.total))}
            </span>
          </div>

          {/*
            Five boxes rather than five bars.
            A bar chart of counts invites reading the widths against each other, which on a funnel
            is exactly the wrong comparison — "5 novos, 0 contatados" is a queue, not a proportion.
          */}
          <div className="stage-grid">
            {(['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'UNQUALIFIED'] as const).map(
              (status) => {
                const count = summary.counts[status] ?? 0;

                return (
                  <div
                    key={status}
                    className={`stage-box${count > 0 ? ' is-filled' : ''} stage-box--${status.toLowerCase()}`}
                  >
                    <span className="stage-box__head">
                      <span className="pill__dot" aria-hidden="true" />
                      <span className="small">{t(`crm.lead.${status}`)}</span>
                    </span>

                    <span className="stage-box__count">{formatNumber(count)}</span>
                  </div>
                );
              },
            )}
          </div>
        </Panel>

        <Panel title={t('crm.kpi.sources')}>
          {summary.sources.length === 0 ? (
            <p className="muted">{t('crm.kpi.noSources')}</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('crm.field.source')}</th>
                  <th className="numeric">{t('crm.kpi.arrived')}</th>
                  <th className="numeric">{t('crm.kpi.customers')}</th>
                  <th className="numeric">{t('crm.kpi.conversion')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.sources.map((source) => (
                  <tr key={source.label ?? '__unknown__'}>
                    <td>
                      <span className="cell-inline">
                        {/* A named channel and "nobody wrote it down" are different facts. */}
                        {source.label === null ? <UnknownGlyph /> : <GlobeGlyph />}
                        {source.label ?? t('crm.kpi.unknownSource')}
                      </span>
                    </td>
                    <td className="numeric">{formatNumber(source.total)}</td>
                    <td className="numeric strong">{formatNumber(source.converted)}</td>
                    <td className="numeric">{formatRate(source.conversionRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {/*
         * Why the ones that did not convert ended, and how long the rest take (Phase 147).
         *
         * The most useful table on a sales screen and the one nobody builds: "preço" forty times
         * is a pricing conversation, "sem retorno" forty times is a process one — and a team
         * cannot fix what it cannot see counted.
         */}
        <Panel title={t('crm.kpi.lossReasons')} description={t('crm.kpi.lossReasonsHint')}>
          <p className="muted small">
            {t('crm.kpi.durations')
              .replace('{qualify}', days(summary.durations.toQualify))
              .replace('{convert}', days(summary.durations.toConvert))
              .replace('{fromContacts}', formatNumber(summary.durations.fromContacts))}
          </p>

          {summary.lossReasons.length === 0 ? (
            <p className="muted">{t('crm.kpi.noLossReasons')}</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('crm.kpi.reason')}</th>
                  <th className="numeric">{t('crm.kpi.times')}</th>
                </tr>
              </thead>
              <tbody>
                {summary.lossReasons.map((row) => (
                  <tr key={row.reason ?? '__none__'}>
                    {/* Closed with nothing written down is itself a finding, so it is named. */}
                    <td>{row.reason ?? t('crm.kpi.noReasonGiven')}</td>
                    <td className="numeric strong">{formatNumber(row.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </>
  );
}

/** A median in days, or a dash: nothing measured is not "zero days". */
function days(value: number | null): string {
  return value === null ? '—' : `${value.toLocaleString('pt-BR')}`;
}

function Figure({
  icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  tone: 'neutral' | 'info' | 'positive';
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric metric--icon">
      <span className={`metric__icon metric__icon--${tone}`} aria-hidden="true">
        {icon}
      </span>

      <span className="cell-stack">
        <span className="metric__label">{label}</span>
        <span className="metric__value">{value}</span>
        {hint && <span className="muted small">{hint}</span>}
      </span>
    </div>
  );
}

/**
 * The quick filters, as filters the server already understands.
 *
 * The same dialect the filter builder and saved views produce, so "meus leads" here and a saved view
 * that says the same thing are the same query rather than two notions of it (context.md section 72).
 */
function buildFilter(input: {
  quick: Quick;
  term: string;
  ownerId: string | null;
}): FilterGroup | undefined {
  const conditions: FilterGroup['conditions'] = [];

  if (input.term) conditions.push({ field: 'name', operator: 'contains', value: input.term });

  switch (input.quick) {
    case 'mine':
      if (input.ownerId)
        conditions.push({ field: 'owner_id', operator: 'eq', value: input.ownerId });
      break;
    case 'new':
      conditions.push({ field: 'status', operator: 'eq', value: 'NEW' });
      break;
    case 'qualified':
      conditions.push({ field: 'status', operator: 'eq', value: 'QUALIFIED' });
      break;
    case 'converted':
      conditions.push({ field: 'status', operator: 'eq', value: 'CONVERTED' });
      break;
    default:
      break;
  }

  return conditions.length > 0 ? { combinator: 'AND', conditions } : undefined;
}
